import { Component, OnInit } from '@angular/core';
import { PackageComparisonService } from '../services/package-comparison.service';

@Component({
  selector: 'prcins-admin-package-comparison',
  templateUrl: './package-comparison.component.html',
  styleUrls: ['./package-comparison.component.css']
})
export class PackageComparisonComponent implements OnInit {

  packageData: any;
  selectedState: string = '';
  states: string[] = ['MA', 'NY', 'CT', 'NH', 'NJ', 'PA'];
  packages: string[] = [];
  features: any[] = [];
  featureGrpCodes: string[] = [];
  showTable: boolean = false;
  loading: boolean = false;
  isDirty: boolean = false;
  notification: { type: string, message: string } | null = null;
  private originalFeatures: any[] = [];
  private changedFeatures: Map<string, { added: string[], removed: string[], modified?: string[] }> = new Map();
  activePopup: { feature: string | null, package: string | null } = { feature: null, package: null };
  private hidePopupTimeout: any = null;

  constructor(private packageService: PackageComparisonService) { }

  ngOnInit(): void {
  }

  getFullStateName(abbrev: string): string {
    const stateMap: { [key: string]: string } = {
      'MA': 'Massachusetts',
      'NY': 'New York',
      'CT': 'Connecticut',
      'NH': 'New Hampshire',
      'NJ': 'New Jersey',
      'PA': 'Pennsylvania'
    };
    return stateMap[abbrev] || abbrev;
  }

  selectState(state: string): void {
    this.selectedState = state;
    this.onSubmit();
  }

  onSubmit(): void {
    this.loadPackageData();
  }

  private loadPackageData(): void {
    this.loading = true;
    this.showTable = false;
    this.isDirty = false;
    this.notification = null;
    this.changedFeatures.clear();
    this.packageService.getPackageComparisonData(this.selectedState).subscribe(data => {
      this.packageData = data;
      
      this.featureGrpCodes = data.featureGrpCodes || [];
      
      const featureData = data.featureComparison || [];
      
      const processedFeatures = featureData.map(feature => {
        const featureCopy = JSON.parse(JSON.stringify(feature));
        
        const packageNames: string[] = [];
        const pinnedGrpCodes: any = {};
        
        if (feature.packages && Array.isArray(feature.packages)) {
          feature.packages.forEach(pkg => {
            if (typeof pkg === 'string') {
              // Simple package name without pinned data
              packageNames.push(pkg);
            } else if (typeof pkg === 'object' && pkg !== null) {
              // Package with pinned data
              const packageName = Object.keys(pkg)[0];
              packageNames.push(packageName);
              pinnedGrpCodes[packageName] = { ...pkg[packageName] };
            }
          });
        }
        
        featureCopy.packages = packageNames;
        featureCopy.pinnedGrpCodes = pinnedGrpCodes;
        
        return featureCopy;
      });
      
      this.features = processedFeatures;
      this.originalFeatures = JSON.parse(JSON.stringify(processedFeatures));
      this.packages = this.getUniquePackages(processedFeatures);
      this.loading = false;
      this.showTable = true;
    });
  }

  private getUniquePackages(features: any[]): string[] {
    const packageSet = new Set<string>();
    if (features) {
      features.forEach(feature => {
        if (feature.packages && Array.isArray(feature.packages)) {
          feature.packages.forEach(pkg => packageSet.add(pkg));
        }
      });
    }
    return Array.from(packageSet);
  }

  isFeatureInPackage(feature: any, packageName: string): boolean {
    return feature.packages && feature.packages.includes(packageName);
  }

  toggleFeatureInPackage(feature: any, packageName: string): void {
    if (!feature.packages) {
      feature.packages = [];
    }
    const index = feature.packages.indexOf(packageName);
    if (index > -1) {
      feature.packages.splice(index, 1);
      if (feature.pinnedGrpCodes) {
        delete feature.pinnedGrpCodes[packageName];
      }
    } else {
      feature.packages.push(packageName);
    }
    this.updateChangedFeatures(feature);
  }

  showPopup(feature: any, packageName: string): void {
    this.cancelHidePopup();
    this.activePopup = { feature: feature.featureName, package: packageName };
  }

  scheduleHidePopup(): void {
    this.hidePopupTimeout = setTimeout(() => {
      this.activePopup = { feature: null, package: null };
    }, 200);
  }

  cancelHidePopup(): void {
    if (this.hidePopupTimeout) {
      clearTimeout(this.hidePopupTimeout);
      this.hidePopupTimeout = null;
    }
  }

  toggleFeatureFlag(feature: any, packageName: string, flagName: string): void {
    this.ensurePinnedGrpCodesExists(feature, packageName);
    
    feature.pinnedGrpCodes[packageName][flagName] = !feature.pinnedGrpCodes[packageName][flagName];
    
    this.updateChangedFeatures(feature);
  }

  private ensurePinnedGrpCodesExists(feature: any, packageName: string): void {
    if (!feature.pinnedGrpCodes) {
      feature.pinnedGrpCodes = {};
    }
    if (!feature.pinnedGrpCodes[packageName]) {
      feature.pinnedGrpCodes[packageName] = {};
      this.featureGrpCodes.forEach(flag => {
        feature.pinnedGrpCodes[packageName][flag] = false;
      });
    }
  }

  getFeatureFlag(feature: any, packageName: string, flagName: string): boolean {
    return feature.pinnedGrpCodes && feature.pinnedGrpCodes[packageName] && feature.pinnedGrpCodes[packageName][flagName] || false;
  }

  isPackagePinned(feature: any, packageName: string): boolean {
    const flags = feature.pinnedGrpCodes && feature.pinnedGrpCodes[packageName];
    return flags ? this.featureGrpCodes.some(flag => flags[flag] === true) : false;
  }

  private updateChangedFeatures(feature: any): void {
    this.notification = null;
    const originalFeature = this.originalFeatures.find(f => f.featureName === feature.featureName);
    if (!originalFeature) return;

    const originalPackages = new Set(originalFeature.packages || []);
    const currentPackages = new Set(feature.packages || []);

    const added = Array.from(currentPackages).filter(p => !originalPackages.has(p));
    const removed = Array.from(originalPackages).filter(p => !currentPackages.has(p));
    
    const modified: string[] = [];
    Array.from(currentPackages).forEach((pkg: any) => {
      const packageName = pkg as string;
      if (originalPackages.has(pkg)) {
        const originalFlags = (originalFeature.pinnedGrpCodes && originalFeature.pinnedGrpCodes[packageName]) || {};
        const currentFlags = (feature.pinnedGrpCodes && feature.pinnedGrpCodes[packageName]) || {};
        
        const flagsChanged = this.featureGrpCodes.some(flag => 
          (originalFlags[flag] || false) !== (currentFlags[flag] || false)
        );
        
        if (flagsChanged) {
          modified.push(packageName);
        }
      }
    });

    if (added.length > 0 || removed.length > 0 || modified.length > 0) {
      this.changedFeatures.set(feature.featureName, { added: added as string[], removed: removed as string[], modified: modified });
    } else {
      this.changedFeatures.delete(feature.featureName);
    }
    this.isDirty = this.changedFeatures.size > 0;
  }

  private buildFlagsObject(feature: any, packageName: string): { [key: string]: boolean } {
    const flags: { [key: string]: boolean } = {};
    this.featureGrpCodes.forEach(flag => {
      flags[flag] = (feature && feature.pinnedGrpCodes && feature.pinnedGrpCodes[packageName] && feature.pinnedGrpCodes[packageName][flag]) || false;
    });
    return flags;
  }

  private hasAnyFlag(feature: any, packageName: string): boolean {
    return feature && feature.pinnedGrpCodes && feature.pinnedGrpCodes[packageName] && 
           this.featureGrpCodes.some(flag => feature.pinnedGrpCodes[packageName][flag]);
  }

  onSave(): void {
    const changedData: any[] = [];
    this.changedFeatures.forEach((changes, featureName) => {
      const feature = this.features.find(f => f.featureName === featureName);
      
      changes.added.forEach(packageName => {
        changedData.push({ 
          featureName, 
          packageName, 
          action: 'add',
          isFeatured: this.hasAnyFlag(feature, packageName) || false,
          pinnedGrpCodes: this.buildFlagsObject(feature, packageName)
        });
      });
      
      changes.removed.forEach(packageName => {
        changedData.push({ 
          featureName, 
          packageName, 
          action: 'remove',
          isFeatured: false,
          pinnedGrpCodes: {}
        });
      });
      
      // Handle modified packages (pinned group codes changes)
      if (changes.modified) {
        changes.modified.forEach(packageName => {
          changedData.push({ 
            featureName, 
            packageName, 
            action: 'update',
            isFeatured: this.hasAnyFlag(feature, packageName) || false,
            pinnedGrpCodes: this.buildFlagsObject(feature, packageName)
          });
        });
      }
    });

    this.packageService.savePackageComparisonData(this.selectedState, changedData).subscribe(
      (response) => {
        console.log('Package comparison data saved successfully', response);
        this.isDirty = false;
        this.changedFeatures.clear();
        this.originalFeatures = JSON.parse(JSON.stringify(this.features));
        this.notification = { type: 'success', message: 'Package comparison data saved successfully.' };
      },
      (error) => {
        console.error('Error saving package comparison data', error);
        this.features = JSON.parse(JSON.stringify(this.originalFeatures));
        this.isDirty = false;
        this.changedFeatures.clear();
        this.notification = { type: 'error', message: 'Failed to update' };
      }
    );
  }
}